import { Input } from '@/components/ui/FormControls';
import {refreshSaleConsumers} from '@/lib/refreshSaleConsumers';
// ─────────────────────────────────────────────
// KLOTHIQ — Data Import & Batch Migration Page
// ─────────────────────────────────────────────

import React, { useState } from 'react';
import Link from '@/components/common/AccessLink';
import {
  Upload,
  FileSpreadsheet,
  Download,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  Database,
  ArrowRight,
  PackageSearch,
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { useQueryClient } from '@tanstack/react-query';

// FEAT-BULK-IMPORT-001: products import moved to its own page
// (/products/import) — validated, product-type-scoped, atomic commit.
// This page's old CSV product importer (no preview, silent upsert-on-
// code-match, random non-conforming SKU fallback) is retired for
// products; customers/sales CSV import below is unchanged.
type ImportTab = 'customers' | 'sales';

interface ParsedRow {
  rowIndex: number;
  data: Record<string, string>;
  isValid: boolean;
  error?: string;
}

export default function DataImportPage() {
  const { organization } = useAuth();
  const orgId = organization?.id;
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState<ImportTab>('customers');
  const [parsedRows, setParsedRows] = useState<ParsedRow[]>([]);
  const [fileName, setFileName] = useState<string>('');
  const [importing, setImporting] = useState<boolean>(false);
  const [importResult, setImportResult] = useState<{ success: number; failed: number } | null>(null);

  // Parse CSV text into array of objects
  const parseCSV = (text: string, tab: ImportTab): ParsedRow[] => {
    const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
    if (lines.length <= 1 || !lines[0]) return [];

    const headers = lines[0].split(',').map((h) => h.trim().replace(/^"|"$/g, ''));
    const rows: ParsedRow[] = [];

    for (let i = 1; i < lines.length; i++) {
      const line = lines[i];
      if (!line) continue;

      const values = line.match(/(".*?"|[^",]+)(?=\s*,|\s*$)/g) || line.split(',');
      const cleanValues = values.map((v) => v.trim().replace(/^"|"$/g, ''));
      
      const rowData: Record<string, string> = {};
      headers.forEach((h, idx) => {
        rowData[h] = cleanValues[idx] ?? '';
      });

      let isValid = true;
      let error = '';

      if (tab === 'customers') {
        if (!rowData.name) {
          isValid = false;
          error = 'Missing customer name';
        }
      } else if (tab === 'sales') {
        if (!rowData.sale_number || !rowData.sku || !rowData.quantity) {
          isValid = false;
          error = 'Missing sale_number, sku, or quantity';
        }
      }

      rows.push({
        rowIndex: i,
        data: rowData,
        isValid,
        error,
      });
    }

    return rows;
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFileName(file.name);
    setImportResult(null);

    const reader = new FileReader();
    reader.onload = (evt) => {
      const text = evt.target?.result as string;
      const parsed = parseCSV(text || '', activeTab);
      setParsedRows(parsed);
    };
    reader.readAsText(file);
  };

  const handleExecuteImport = async () => {
    if (!orgId || parsedRows.length === 0) return;
    const validRows = parsedRows.filter((r) => r.isValid);
    if (validRows.length === 0) return;

    setImporting(true);
    let successCount = 0;
    let failCount = 0;

    try {
      if (activeTab === 'customers') {
        for (const row of validRows) {
          const d = row.data;
          const { error } = await supabase.from('customers').insert({
            organization_id: orgId,
            name: d.name || 'Unnamed Customer',
            phone: d.phone || null,
            email: d.email || null,
            address: d.address || null,
          });
          if (!error) successCount++;
          else failCount++;
        }
      }

      setImportResult({ success: successCount, failed: failCount });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      void refreshSaleConsumers(queryClient);
    } catch (e) {
      console.error('Import error:', e);
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-6 max-w-6xl mx-auto pb-12">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground flex items-center gap-2">
            <Database className="h-6 w-6 text-primary" /> Data Migration & Bulk Importer
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Import historical spreadsheets for products, variants, initial stock counts, and customers.
          </p>
        </div>
      </div>

      {/* Products import now lives at its own validated, product-type-scoped page */}
      <Link
        to="/products/import"
        className="obsidian-surface obsidian-interactive flex items-center justify-between rounded-xl border border-primary/30 bg-primary/5 p-5 shadow-sm hover:bg-primary/10 transition-colors"
      >
        <div className="flex items-center gap-3">
          <PackageSearch className="h-6 w-6 text-primary" />
          <div>
            <p className="text-sm font-semibold text-foreground">Products & Inventory Import moved</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Now a validated, product-type-scoped .xlsx flow with a preview step — go to Products → Import.
            </p>
          </div>
        </div>
        <ArrowRight className="h-4 w-4 text-primary shrink-0" />
      </Link>

      {/* Tabs */}
      <div className="flex border-b border-border gap-2">
        <button
          onClick={() => { setActiveTab('customers'); setParsedRows([]); setFileName(''); }}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
            activeTab === 'customers'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          <FileSpreadsheet className="h-4 w-4" /> Customers Directory
        </button>
        <button
          onClick={() => { setActiveTab('sales'); setParsedRows([]); setFileName(''); }}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
            activeTab === 'sales'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          <FileSpreadsheet className="h-4 w-4" /> Historical Sales Orders
        </button>
      </div>

      {/* Import Instructions & Template Download Card */}
      <div className="obsidian-surface rounded-xl border border-border bg-card p-5 shadow-sm space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
            Step 1: Download Standard CSV Template
          </h2>
          <a
            href={`/templates/${activeTab}_import_template.csv`}
            download
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-secondary text-secondary-foreground hover:bg-secondary/80 transition-colors"
          >
            <Download className="h-3.5 w-3.5" /> Download {activeTab.toUpperCase()} CSV Template
          </a>
        </div>
        <p className="text-xs text-muted-foreground leading-relaxed">
          Ensure your spreadsheet columns exactly match the CSV template headers. All monetary values should be in Indian Rupees (₹).
        </p>
      </div>

      {/* Upload Box */}
      <div className="obsidian-surface rounded-xl border-2 border-dashed border-border bg-card/50 p-8 text-center hover:bg-card/80 transition-colors">
        <Upload className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
        <h3 className="text-base font-semibold text-foreground">
          Upload {activeTab.toUpperCase()} CSV Spreadsheet File
        </h3>
        <p className="text-xs text-muted-foreground mt-1 mb-4">Select or drag `.csv` file exported from Excel or Google Sheets</p>
        <label className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 cursor-pointer shadow-sm">
          <FileSpreadsheet className="h-4 w-4" /> Choose CSV File
          <Input
            type="file"
            accept=".csv"
            onChange={handleFileUpload}
            className="hidden"
            helpLabel="Choose CSV File"
          />
        </label>
        {fileName && (
          <p className="text-xs font-medium text-emerald-400 mt-3">Selected: {fileName}</p>
        )}
      </div>

      {/* Preview Table & Validation */}
      {parsedRows.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold text-foreground">
              Step 2: CSV Validation Preview ({parsedRows.filter((r) => r.isValid).length} valid of {parsedRows.length} rows)
            </h3>
            <button
              onClick={handleExecuteImport}
              disabled={importing || parsedRows.filter((r) => r.isValid).length === 0}
              className="inline-flex items-center gap-2 px-5 py-2 text-sm font-bold rounded-lg bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-50 transition-colors shadow"
            >
              {importing ? (
                <>
                  <RefreshCw className="h-4 w-4 animate-spin" /> Importing Records...
                </>
              ) : (
                <>
                  Execute Batch Import <ArrowRight className="h-4 w-4" />
                </>
              )}
            </button>
          </div>

          {importResult && (
            <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm flex items-center justify-between">
              <span className="font-semibold flex items-center gap-2">
                <CheckCircle className="h-5 w-5" /> Successfully imported {importResult.success} records into database!
              </span>
              {importResult.failed > 0 && (
                <span className="text-rose-400 text-xs font-medium">({importResult.failed} failed/skipped)</span>
              )}
            </div>
          )}

          <div className="obsidian-surface rounded-xl border border-border bg-card overflow-hidden shadow-sm">
            <div className="overflow-x-auto max-h-96">
              <table className="w-full text-xs text-left">
                <thead className="bg-muted/50 text-muted-foreground uppercase text-[10px] tracking-wider sticky top-0">
                  <tr>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Row</th>
                    {parsedRows[0]?.data && Object.keys(parsedRows[0].data).map((key) => (
                      <th key={key} className="px-4 py-3">{key}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {parsedRows.map((row) => (
                    <tr key={row.rowIndex} className={row.isValid ? 'hover:bg-muted/30' : 'bg-rose-500/10'}>
                      <td className="px-4 py-2.5 whitespace-nowrap">
                        {row.isValid ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                            <CheckCircle className="h-3 w-3" /> Valid
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-rose-500/10 text-rose-400 border border-rose-500/20" title={row.error}>
                            <AlertCircle className="h-3 w-3" /> Invalid
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 font-mono text-muted-foreground">#{row.rowIndex}</td>
                      {Object.values(row.data).map((val, idx) => (
                        <td key={idx} className="px-4 py-2.5 text-foreground truncate max-w-[150px]">{val}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
